import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { TranslateService } from '@ngx-translate/core';

import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { prependEmptyOption } from '../models/coaching.global.model';
import { CommitmentHistory, PerformanceGoal } from '../models/oa.history.model';
import { SortDirection, SortingField } from '../models/sorting.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { CoachingService } from '../services/coaching.service';

import { AbstractOAHistory } from './oa-history-page.component';

@Component({
    selector: 'cmps-commitments-history',
    templateUrl: './commitments-history.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommitmentsHistoryComponent extends AbstractOAHistory<CommitmentHistory> {
    constructor(
        protected _coachInsightService: CoachingInsightWidgetService,
        protected _coachingService: CoachingService,
        protected cd: ChangeDetectorRef,
        protected _translateService: TranslateService,
    ) {
        super(_coachInsightService, _coachingService, cd, _translateService);
    }

    goals: PerformanceGoal[];

    allRecordsHidden = false;
    hasPendingData = false;
    failLoad = '';

    displayGoals = false;

    defaultSortIndex: number = 4; // sessionDate;

    i18n = TranslationKeyMap;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    protected initData() {
        this.hasPendingData = true;

        this.sortFields = [
            new SortingField('behaviour', 'string'),
            new SortingField('subBehaviour', 'string'),
            new SortingField('coachName', 'string'),
            new SortingField('sessionTypeName', 'string'),
            new SortingField('sessionDate', 'date', SortDirection.DESC),
            new SortingField('statusName', 'string'),
            new SortingField('aging', 'number'),
            new SortingField('startDate', 'date'),
            new SortingField('lastUpdated', 'date'),
            new SortingField('observationCount', 'number'),
            new SortingField('evaluationCount', 'number'),
            new SortingField('consistency', 'number'),
            new SortingField('proficiency', 'number'),
            new SortingField('goalCount', 'number'),
        ];

        const x = this._coachingService.retrieveCommitmentHistory(
            this.performerId,
            this._coachInsightService.getPeriodTypeId(),
            this.numRecords,
        );
        this.sub = x.subscribe(
            (s) => {
                if (s) {
                    this.data = s.history;
                    this.goals = s.goals;
                    let sessiontypes = s.sessionTypes;
                    let coaches = s.coaches;
                    let behaviours = s.behaviours;
                    let subbehaviours = s.subBehaviours;
                    const statuses = [];

                    if (this.data) {
                        this.data.forEach((c) => {
                            c.rowSpan = 1;
                            c.display = true;

                            const goals = this.goals.filter(
                                (g) => g.coachingEventId === c.coachingEventId,
                            );

                            // c.statusName = OAStatuses[c.statusId-1];

                            c.goals = goals.length ? goals : undefined;
                            // if(c.goals) {
                            //     c.goals = c.goals.map(g=>{
                            //         g.goalStatus = OAStatuses[g.goalStatus as number - 1];
                            //         return g;
                            //     })
                            // }
                            const statusIdStr = c.statusId.toString();
                            const found = statuses.find(
                                (d) => d.value === statusIdStr,
                            );
                            if (!found) {
                                const opt: IOption = {
                                    label: c.statusName,
                                    value: statusIdStr,
                                };
                                statuses.push(opt);
                            }
                        });
                    }

                    if (this.filterOptions) {
                        sessiontypes = sessiontypes.map((d) => {
                            if (d.sessionTypeId) {
                                d.value = d.sessionTypeId.toString();
                            }
                            if (d.sessionTypeName) {
                                d.label = d.sessionTypeName;
                            }
                            return d;
                        });
                        behaviours = behaviours.map((d) => {
                            if (d.behaviourId) {
                                d.value = d.behaviourName;
                            }
                            if (d.behaviourName) {
                                d.label = d.behaviourName;
                            }
                            return d;
                        });
                        subbehaviours = subbehaviours.map((d) => {
                            if (d.subBehaviourId) {
                                d.value = d.subBehaviourName;
                            }
                            if (d.subBehaviourName) {
                                d.label = d.subBehaviourName;
                            }
                            return d;
                        });
                        coaches = coaches.map((d) => {
                            if (d.coachId) {
                                d.value = d.coachId.toString();
                            }
                            if (d.coachName) {
                                d.label = d.coachName;
                            }
                            return d;
                        });

                        this.filterOptions.SessionTypes = sessiontypes;
                        this.filterOptions.Behaviours = behaviours;
                        this.filterOptions.SubBehaviours = subbehaviours;
                        this.filterOptions.Coaches = coaches;
                        this.filterOptions.Statuses = statuses;

                        prependEmptyOption(
                            this.filterOptions.SessionTypes,
                            this.placeholderText,
                        );
                        prependEmptyOption(
                            this.filterOptions.Behaviours,
                            this.placeholderText,
                        );
                        prependEmptyOption(
                            this.filterOptions.SubBehaviours,
                            this.placeholderText,
                        );
                        prependEmptyOption(
                            this.filterOptions.Coaches,
                            this.placeholderText,
                        );
                        prependEmptyOption(
                            this.filterOptions.Statuses,
                            this.placeholderText,
                        );
                    }
                    this.sortByFieldDefault();
                    this.getDisplayable();
                } else {
                    this.failLoad = this._translateService.instant(
                        TranslationKeyMap._7447_FAILED_TO_LOAD_DATA,
                    );
                }
                this.emitDisplayStatus();
                this.hasPendingData = false;
                this.cd.detectChanges();
            },
            (e) => {
                this.failLoad = this._translateService.instant(
                    TranslationKeyMap._7447_FAILED_TO_LOAD_DATA,
                );
                this.hasPendingData = false;
                this.cd.detectChanges();
            },
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    showSubs() {
        this.displayGoals = true;
        this.data.forEach((c) => {
            // c.rowSpan = (c.goals) ? (c.goals.length > 1 ? c.goals.length - 1 : 1) : 1;
            c.rowSpan = c.goals ? c.goals.length : 1;
        });
        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    hideSubs() {
        this.displayGoals = false;
        this.data.forEach((c) => {
            c.rowSpan = 1;
        });
        this.cd.detectChanges();
    }
}
